import { healthcardSchema } from '@billing/validation';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Select,
  Table,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import {
  healthcardSigningAuthorityOptions,
  provinceOptions,
} from 'constants/provinceConstants';

import { Healthcard, Patient } from 'interfaces';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useEffect, useState } from 'react';
import { useCurrentSiteStore } from 'store/SiteStore';
import { nowISODate, toJSDate } from 'utils/dateUtils';
import { v4 as uuid } from 'uuid';
import { flattenError } from 'zod';

export const PatientColumns = () => {
  const { organization } = useCurrentSiteStore();
  const columns: MRT_ColumnDef<Patient>[] = [
    {
      accessorKey: 'lastName',
      header: 'Last Name',
      accessorFn: (row) => row.lastName || '',
      mantineEditTextInputProps: ({ row }) => {
        return {
          onChange: (e) => {
            row._valuesCache.lastName = e.target.value;
          },
          required: true,
        };
      },
    },
    {
      accessorKey: 'firstName',
      header: 'First Name',
      accessorFn: (row) => row.firstName,
      mantineEditTextInputProps: ({ row }) => {
        return {
          onChange: (e) => {
            row._valuesCache.firstName = e.target.value;
          },
          required: true,
        };
      },
    },
    {
      accessorKey: 'dateOfBirth',
      header: 'Date of Birth',
      Edit: ({ row }) => {
        const [date, setDate] = useState<string | null>(
          row.original?.dateOfBirth ? row.original.dateOfBirth : null
        );
        return (
          <DateInput
            value={date}
            onChange={(date) => {
              setDate(date);
              row._valuesCache.dateOfBirth = date ?? undefined;
            }}
            maxDate={toJSDate(nowISODate())}
            label="Date of Birth"
            clearable
            required
          />
        );
      },
      enableColumnFilter: false,
    },
    {
      accessorKey: 'address',
      header: 'Address',
      mantineEditTextInputProps: ({ row }) => {
        return {
          onChange: (e) => {
            row._valuesCache.address = e.target.value;
          },
        };
      },
      enableColumnFilter: false,
    },
    {
      accessorKey: 'city',
      header: 'City',
      mantineEditTextInputProps: ({ row }) => {
        return {
          onChange: (e) => {
            row._valuesCache.city = e.target.value;
          },
        };
      },
      enableColumnFilter: false,
    },
    {
      accessorKey: 'province',
      header: 'Province',
      editVariant: 'select',
      mantineEditSelectProps: ({ row }) => {
        return {
          onChange: (e) => {
            row._valuesCache.province = e;
          },
          data: provinceOptions,
          value: row._valuesCache.province,
          required: true,
        };
      },
      enableColumnFilter: false,
    },
    {
      accessorKey: 'country',
      header: 'Country',
      mantineEditTextInputProps: ({ row }) => {
        return {
          onChange: (e) => {
            row._valuesCache.country = e.target.value;
          },
        };
      },
    },
    {
      accessorKey: 'postalCode',
      header: 'Postal Code',
      mantineEditTextInputProps: ({ row }) => {
        return {
          onChange: (e) => {
            row._valuesCache.postalCode = e.target.value;
          },
        };
      },
      enableColumnFilter: false,
    },
    {
      accessorKey: 'email',
      header: 'Email',
      mantineEditTextInputProps: ({ row }) => {
        return {
          onChange: (e) => {
            row._valuesCache.email = e.target.value;
          },
        };
      },
      enableColumnFilter: false,
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      mantineEditTextInputProps: ({ row }) => {
        return {
          onChange: (e) => {
            row._valuesCache.phone = e.target.value;
          },
        };
      },

      enableColumnFilter: false,
    },
    {
      accessorKey: 'sex',
      header: 'Sex',
      editVariant: 'select',
      mantineEditSelectProps: ({ row }) => {
        return {
          onChange: (e) => {
            row._valuesCache.sex = e;
          },
          data: [
            { label: 'Male', value: 'M' },
            { label: 'Female', value: 'F' },
            { label: 'Other', value: 'O' },
          ],
          value: row._valuesCache.sex,
          required: true,
        };
      },
      enableColumnFilter: false,
    },
    {
      accessorKey: 'maritalStatus',
      header: 'Marital Status',
      editVariant: 'select',
      mantineEditSelectProps: ({ row }) => {
        return {
          onChange: (e) => {
            row._valuesCache.maritalStatus = e;
          },
          data: [
            { label: 'Single', value: 'S' },
            { label: 'Married', value: 'M' },
            { label: 'Divorced', value: 'D' },
            { label: 'Widowed', value: 'W' },
            { label: 'Separated', value: 'Sep' },
          ],
          value: row._valuesCache.maritalStatus,
        };
      },
      enableColumnFilter: false,
    },
    {
      accessorKey: 'healthcards',
      header: 'Health Card Number',
      size: 300,
      Cell: ({ cell }) => {
        const cards = (cell.getValue() as Healthcard[]) ?? [];
        return (
          <Group gap="xs" wrap="wrap">
            {cards.map((hc, i) => (
              <Badge key={i} variant="dot">
                {hc.number || '—'} · ({hc.registration || '—'}) ·{' '}
                {hc.province || '—'}
              </Badge>
            ))}
          </Group>
        );
      },
      Edit: ({ row }) => {
        const [healthcards, setHealthcards] = useState<Healthcard[]>(
          row.original?.healthcards.length > 0 ? row.original.healthcards : []
        );
        useEffect(() => {
          row._valuesCache.healthcards = healthcards;
        }, [healthcards]);
        const setField = (
          index: number,
          field: keyof Healthcard,
          value: string | null
        ) => {
          setHealthcards((prev) =>
            prev.map((hc, i) =>
              i === index ? { ...hc, [field]: value ?? '' } : hc
            )
          );
          validateRow(index);
        };

        const addHealthCard = ({ number, registration, province }) => {
          setHealthcards((prev) => [
            ...prev,
            {
              number,
              registration,
              province,
              id: uuid(),
              patientID: row.original.id,
            },
          ]);
        };

        const removeHealthCard = async (index: number) => {
          setHealthcards((prev) => prev.filter((_, i) => i !== index));
        };

        const validateRow = (index: number) => {
          const hc = healthcards[index];
          return healthcardSchema.safeParse(hc).success;
        };

        const fieldError = (index: number) => {
          const hc = healthcards[index];
          const result = healthcardSchema.safeParse(hc);
          if (!result.success) {
            const fieldError = flattenError(result.error);
            if (fieldError) {
              return fieldError;
            }
          }
          return null;
        };

        return (
          <Card withBorder>
            <Table withRowBorders={true} verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Province</Table.Th>
                  <Table.Th>Number</Table.Th>
                  <Table.Th>Registration</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {healthcards.map((hc, idx) => {
                  const isValid = validateRow(idx);
                  const numberError = fieldError(idx);
                  const registrationError = fieldError(idx);
                  const provinceError = fieldError(idx);

                  return (
                    <Table.Tr key={`${row.id}-hc-${idx}`}>
                      <Table.Td style={{ minWidth: 180 }}>
                        <Select
                          placeholder="Select"
                          data={healthcardSigningAuthorityOptions}
                          value={hc.province ?? null}
                          onChange={(value) => setField(idx, 'province', value)}
                          searchable
                          clearable
                          error={
                            isValid
                              ? undefined
                              : provinceError.fieldErrors?.province
                          }
                        />
                      </Table.Td>
                      <Table.Td style={{ minWidth: 220 }}>
                        <TextInput
                          placeholder="e.g. 1234567890"
                          value={hc.number ?? ''}
                          onChange={(e) =>
                            setField(idx, 'number', e.target.value)
                          }
                          error={
                            isValid
                              ? undefined
                              : numberError.fieldErrors?.number
                          }
                          disabled={!hc.province}
                        />
                      </Table.Td>
                      <Table.Td style={{ minWidth: 180 }}>
                        <TextInput
                          placeholder="e.g. 202501"
                          value={hc.registration ?? ''}
                          onChange={(e) =>
                            setField(idx, 'registration', e.target.value)
                          }
                          error={
                            isValid
                              ? undefined
                              : registrationError.fieldErrors?.registration
                          }
                          disabled={!hc.province}
                        />
                      </Table.Td>
                      <Table.Td width={44}>
                        <ActionIcon
                          aria-label="Remove health card"
                          variant="subtle"
                          color="red"
                          onClick={() => removeHealthCard(idx)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Button
                      leftSection={<IconPlus size={16} />}
                      variant="light"
                      onClick={() =>
                        addHealthCard({
                          number: '',
                          registration: '',
                          province: '',
                        })
                      }
                    >
                      Add health card
                    </Button>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Card>
        );
      },
    },
    {
      accessorKey: 'organizationID',
      header: 'Organization',
      accessorFn: () => (organization ? organization.id : ''),
      Cell: () => <>{organization?.name || '—'}</>,
      mantineEditTextInputProps: {
        disabled: true,
        value: organization?.name || '',
      },
      enableColumnFilter: false,
    },
  ];
  return columns;
};
